import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Puzzle, RefreshCw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { HealthDot } from '@/components/extensions/HealthDot'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { extensionsApi } from '@/services/api/extensions'

const EXTENSION_ID = 'geocoding'

function formatRelative(
  seconds: number,
  t: (k: string, opts?: Record<string, unknown>) => string
): string {
  if (seconds < 5) return t('extensions.justNow')
  if (seconds < 60) return t('extensions.secondsAgo', { n: seconds })
  return t('extensions.minutesAgo', { n: Math.floor(seconds / 60) })
}

export default function GeocodingExtensionPage() {
  const { t } = useTranslation()

  const extQuery = useQuery({
    queryKey: ['extensions', EXTENSION_ID],
    queryFn: () => extensionsApi.get(EXTENSION_ID),
  })

  const [lastChecked, setLastChecked] = useState<number | null>(null)
  const healthQuery = useQuery({
    queryKey: ['extensions', EXTENSION_ID, 'health'],
    queryFn: async () => {
      const data = await extensionsApi.health(EXTENSION_ID)
      setLastChecked(Date.now())
      return data
    },
    staleTime: 30_000,
  })

  const status = healthQuery.data?.status ?? 'unknown'
  const url = extQuery.data?.url ?? ''
  const host = useMemo(() => {
    try {
      return url ? new URL(url).host : ''
    } catch {
      return url
    }
  }, [url])

  const lastCheckedLabel = useLastCheckedLabel(lastChecked)

  return (
    <PageShell
      title={t('extensions.geocoding.title')}
      summary={t('extensions.geocoding.description')}
      icon={Puzzle}
    >
      <div className="space-y-4">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 w-fit px-2 text-muted-foreground"
        >
          <Link to="/extensions">
            <ArrowLeft className="size-4" />
            {t('extensions.backToList')}
          </Link>
        </Button>
        <Card className="p-4 md:p-5">
          <header className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-foreground">
              {t('extensions.geocoding.status.title')}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => healthQuery.refetch()}
              disabled={healthQuery.isFetching}
            >
              <RefreshCw
                className={healthQuery.isFetching ? 'mr-1.5 size-4 animate-spin' : 'mr-1.5 size-4'}
              />
              {t('extensions.refresh')}
            </Button>
          </header>
          <dl className="mt-4 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs md:grid-cols-[max-content_1fr] md:gap-x-6">
            <dt className="text-muted-foreground">{t('extensions.endpoint')}</dt>
            <dd className="font-mono text-foreground max-md:break-all">{url}</dd>
            <dt className="text-muted-foreground">
              {t('extensions.health.healthy').replace(/.*/, '')}
            </dt>
            <dd>
              <HealthDot status={status} />
            </dd>
            <dt className="text-muted-foreground">{t('extensions.lastCheck')}</dt>
            <dd className="text-foreground">{lastCheckedLabel}</dd>
            <dt className="text-muted-foreground">{t('extensions.latency')}</dt>
            <dd className="text-foreground">
              {healthQuery.data ? `${healthQuery.data.latency_ms} ms` : '—'}
            </dd>
            {healthQuery.data?.error && (
              <>
                <dt className="text-muted-foreground">{t('extensions.geocoding.status.error')}</dt>
                <dd className="text-destructive max-md:break-words">{healthQuery.data.error}</dd>
              </>
            )}
          </dl>
        </Card>

        <TestQueryCard status={status} host={host} />
      </div>
    </PageShell>
  )
}

function useLastCheckedLabel(lastChecked: number | null): string {
  const { t } = useTranslation()
  if (!lastChecked) return '—'
  const seconds = Math.max(0, Math.floor((Date.now() - lastChecked) / 1000))
  return formatRelative(seconds, t)
}

function TestQueryCard({ status, host }: { status: string; host: string }) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const [limit, setLimit] = useState(5)
  const disabled = status === 'unreachable'

  const mutation = useMutation({
    mutationFn: () => extensionsApi.geocode(q, limit),
  })

  return (
    <Card className="space-y-4 p-4 md:p-5">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">
          {t('extensions.geocoding.test.title')}
        </h2>
      </header>

      {/* Phone: the address takes the first row, limit + submit share the second. */}
      <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 md:grid-cols-[1fr_120px_max-content]">
        <div className="col-span-2 space-y-1.5 md:col-span-1">
          <Label htmlFor="geocode-q" className="text-xs">
            {t('extensions.geocoding.test.queryLabel')}
          </Label>
          <Input
            id="geocode-q"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Na bojisti 12 Brno"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="geocode-limit" className="text-xs">
            {t('extensions.geocoding.test.limitLabel')}
          </Label>
          <Input
            id="geocode-limit"
            type="number"
            min={1}
            max={10}
            value={limit}
            onChange={e => setLimit(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate()}
            disabled={disabled || !q.trim() || mutation.isPending}
            className="max-md:w-full"
          >
            {t('extensions.geocoding.test.submit')}
          </Button>
        </div>
      </div>

      {disabled && (
        <p className="text-xs text-destructive">{t('extensions.geocoding.test.submitDisabled')}</p>
      )}
      {host && (
        <p className="text-xs text-muted-foreground">
          {t('extensions.geocoding.test.privacyHint', { host })}
        </p>
      )}

      {mutation.isError && (
        <p className="text-xs text-destructive">{(mutation.error as Error).message}</p>
      )}

      {mutation.data && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {t('extensions.geocoding.test.summary', {
              found: mutation.data.found,
              ms: mutation.data.took_ms,
            })}
          </p>
          {mutation.data.results.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('extensions.geocoding.test.noResults')}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs max-md:min-w-[40rem]">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('extensions.geocoding.test.columns.display')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('extensions.geocoding.test.columns.street')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('extensions.geocoding.test.columns.city')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('extensions.geocoding.test.columns.postcode')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('extensions.geocoding.test.columns.country')}
                    </th>
                    <th className="px-3 py-2 text-left font-medium">
                      {t('extensions.geocoding.test.columns.coord')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {mutation.data.results.map(r => (
                    <tr key={r.id}>
                      <td className="px-3 py-2 text-foreground">{r.display}</td>
                      <td className="px-3 py-2 text-foreground">
                        {[r.street, r.housenumber].filter(Boolean).join(' · ')}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{r.city ?? '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.postcode ?? '—'}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.country ?? '—'}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">
                        {r.lat.toFixed(4)}, {r.lon.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
